import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface ChartData {
  date: string;
  revenue: number;
  orders_count: number;
}

const RevenueChart = () => {
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('daily');

  const { data: chartData = [], isLoading } = useQuery({
    queryKey: ['revenue-chart', viewType],
    queryFn: async () => {
      const tableName = viewType === 'daily' ? 'daily_revenue_chart' : 'monthly_revenue_chart';
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order(viewType === 'daily' ? 'date' : 'month');

      if (error) throw error;
      
      return data.map(item => ({
        date: viewType === 'daily' ? item.date : item.month,
        revenue: Number(item.revenue),
        orders_count: item.orders_count
      })) as ChartData[];
    },
    refetchInterval: 60000, // Refetch every minute
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Revenue Chart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-muted animate-pulse rounded"></div>
        </CardContent>
      </Card>
    );
  }

  const maxRevenue = Math.max(...chartData.map(d => d.revenue));
  const hasData = chartData.some(d => d.revenue > 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Revenue Trend
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant={viewType === 'daily' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('daily')}
            >
              Daily
            </Button>
            <Button
              variant={viewType === 'monthly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('monthly')}
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No revenue data available yet</p>
              <p className="text-sm">Revenue will appear when orders are marked as delivered</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Simple Bar Chart */}
            <div className="h-64 flex items-end justify-between gap-1 px-2">
              {chartData.map((item, index) => {
                const height = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;
                const dateLabel = viewType === 'daily' 
                  ? format(new Date(item.date), 'dd/MM')
                  : format(new Date(item.date), 'MMM yy');
                
                return (
                  <div key={index} className="flex flex-col items-center flex-1 max-w-12">
                    <div className="w-full flex flex-col justify-end h-48 mb-2">
                      <div
                        className="w-full bg-primary rounded-t transition-all duration-300 hover:bg-primary/80 relative group"
                        style={{ height: `${height}%`, minHeight: item.revenue > 0 ? '4px' : '0px' }}
                      >
                        {item.revenue > 0 && (
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            ₹{item.revenue.toLocaleString('en-IN')}
                            <br />
                            {item.orders_count} orders
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground transform -rotate-45 origin-center mt-1">
                      {dateLabel}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded"></div>
                <span>Revenue (₹)</span>
              </div>
              <span>•</span>
              <span>
                {viewType === 'daily' ? 'Last 30 days' : 'Last 12 months'}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueChart;